import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type ProductPaymentDirection = "debit" | "credit";

export type ProductPaymentTransactionType =
  | "purchase"
  | "investment"
  | "additional_cost"
  | "sale"
  | "token"
  | "installment"
  | "refund"
  | "adjustment";

export type ProductPaymentMethod = "cash" | "bank" | "cheque" | "online";

export type ProductPaymentRecord = {
  id: number;
  product_id: number;
  sale_id?: number | null;
  party_id?: number | null;
  party_name?: string | null;
  direction: ProductPaymentDirection;
  amount: number;
  debit: number;
  credit: number;
  transaction_type: ProductPaymentTransactionType | null;
  transaction_type_label: string | null;
  payment_date: string | null;
  payment_method?: ProductPaymentMethod | null;
  payment_method_label?: string | null;
  bank_id?: number | null;
  bank_name?: string | null;
  transaction_reference_no?: string | null;
  pay_order_no?: string | null;
  notes?: string | null;
  reference_type?: string | null;
  reference_id?: number | null;
  scheduled_payment_id?: number | null;
  created_at?: string | null;
};

const SCHEDULED_REFERENCE_TYPES = new Set(["product", "purchase_cost_line", "deal_partner"]);

export function isScheduledProductPayment(row: ProductPaymentRecord): boolean {
  return SCHEDULED_REFERENCE_TYPES.has(row.reference_type || "");
}

export function isActualProductPayment(row: ProductPaymentRecord): boolean {
  return row.reference_type === "journal_entry";
}

function amountAppliedToScheduledRow(
  payment: ProductPaymentRecord,
  scheduledRow: ProductPaymentRecord,
): number {
  const isPayable = scheduledRow.direction === "credit";
  return isPayable ? Number(payment.debit) : Number(payment.credit);
}

/** Map each scheduled payment row id -> total amount already paid/received against it. */
export function buildScheduledPaymentAllocations(
  scheduled: ProductPaymentRecord[],
  actual: ProductPaymentRecord[],
): Map<number, number> {
  const allocations = new Map<number, number>();
  for (const row of scheduled) {
    allocations.set(row.id, 0);
  }

  const journalActual = actual.filter(isActualProductPayment);

  for (const payment of journalActual) {
    if (payment.scheduled_payment_id != null) {
      const row = scheduled.find((candidate) => candidate.id === payment.scheduled_payment_id);
      if (!row) {
        continue;
      }
      const prev = allocations.get(row.id) ?? 0;
      allocations.set(row.id, prev + amountAppliedToScheduledRow(payment, row));
      continue;
    }

    const isPay = Number(payment.debit) > Number(payment.credit);
    const amount = isPay ? Number(payment.debit) : Number(payment.credit);
    if (amount <= 0) {
      continue;
    }

    const candidates = scheduled.filter((row) => {
      const rowIsPayable = row.direction === "credit";
      if (isPay !== rowIsPayable) {
        return false;
      }
      if (row.party_id !== payment.party_id) {
        return false;
      }
      if (row.transaction_type !== payment.transaction_type) {
        return false;
      }
      const allocated = allocations.get(row.id) ?? 0;
      return allocated + 0.009 < Number(row.amount);
    });

    let remaining = amount;
    for (const row of candidates) {
      const allocated = allocations.get(row.id) ?? 0;
      const need = Math.max(0, Number(row.amount) - allocated);
      const apply = Math.min(remaining, need);
      allocations.set(row.id, allocated + apply);
      remaining -= apply;
      if (remaining <= 0.009) {
        break;
      }
    }
  }

  return allocations;
}

export function scheduledRowOutstanding(
  row: ProductPaymentRecord,
  allocations: Map<number, number>,
): number {
  const allocated = allocations.get(row.id) ?? 0;
  return Math.max(0, Number(row.amount) - allocated);
}

export type ProductPaymentsResponse = {
  data: ProductPaymentRecord[];
  meta: {
    total_debit: number;
    total_credit: number;
    net: number;
  };
};

export function productPaymentErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export async function fetchProductPayments(productId: string | number): Promise<ProductPaymentsResponse> {
  const response = await api.get<ProductPaymentsResponse>(
    `/api/v1/catalog/products/${encodeURIComponent(String(productId))}/payments`,
  );
  return {
    data: Array.isArray(response.data?.data) ? response.data.data : [],
    meta: {
      total_debit: Number(response.data?.meta?.total_debit ?? 0),
      total_credit: Number(response.data?.meta?.total_credit ?? 0),
      net: Number(response.data?.meta?.net ?? 0),
    },
  };
}

export async function recordProductPayment(
  productId: string | number,
  payload: {
    amount: number;
    payment_date: string;
    payment_method: string | null;
    bank_name: string | null;
    transaction_reference_no: string | null;
    pay_order_no: string | null;
    notes: string | null;
    action: "pay" | "receive";
    party_id: number;
    transaction_type: string;
    scheduled_payment_id: number;
  }
): Promise<any> {
  const response = await api.post(`/api/v1/catalog/products/${encodeURIComponent(String(productId))}/payments`, payload);
  return response.data;
}
